import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/config/env';
import { tokenStore } from '@/lib/tokenStore';
import type { ApiErrorBody, ApiErrorCode, ApiSuccess, FieldError } from '@/types/api';

/**
 * A normalised error every screen can rely on.
 *
 * Axios errors are awkward to work with in components (network failure vs HTTP
 * error vs cancelled request all look different). Everything that leaves this
 * module is an ApiRequestError with a `code` the UI can switch on.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly fields: FieldError[];

  constructor(message: string, status: number, code: ApiErrorCode, fields: FieldError[] = []) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  /** Field errors keyed by field name — feeds straight into react-hook-form. */
  get fieldMap(): Record<string, string> {
    return this.fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.field] = field.message;
      return acc;
    }, {});
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 20_000,
  withCredentials: true, // sends the httpOnly refresh cookie
  headers: { 'Content-Type': 'application/json' },
});

// --- request: attach the access token ---------------------------------------
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- response: transparently refresh an expired access token -----------------

/** Requests that arrived while a refresh was already running. */
let refreshPromise: Promise<string | null> | null = null;

async function requestNewAccessToken(): Promise<string | null> {
  try {
    const { data } = await axios.post<ApiSuccess<{ accessToken: string } | null>>(
      `${env.apiBaseUrl}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    // Null means the cookie is gone altogether — the session is over.
    if (!data.data) {
      tokenStore.notifyExpired();
      return null;
    }
    const token = data.data.accessToken;
    tokenStore.set(token);
    return token;
  } catch (error) {
    // Only a 401 means the server refused the session. A rate limit, a 5xx or a
    // dropped connection says nothing about it — the cookie is still good — so
    // signing the person out then would send them to the login page for no
    // reason, and a reload a minute later would quietly let them back in.
    if (error instanceof AxiosError && error.response?.status === 401) {
      tokenStore.notifyExpired();
      return null;
    }
    if (error instanceof AxiosError && error.response) {
      const body = error.response.data as ApiErrorBody | undefined;
      throw new ApiRequestError(
        body?.message ?? 'Something went wrong. Please try again.',
        error.response.status,
        body?.code ?? 'INTERNAL_ERROR',
      );
    }
    throw new ApiRequestError('Cannot reach the server. Please check your internet connection.', 0, 'NETWORK_ERROR');
  }
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as RetriableConfig | undefined;

    // No response at all: server down, DNS failure, or the request timed out.
    if (!error.response) {
      throw new ApiRequestError(
        'Cannot reach the server. Please check your internet connection.',
        0,
        'NETWORK_ERROR',
      );
    }

    const { status, data } = error.response;
    const code = data?.code ?? 'INTERNAL_ERROR';

    // Access token expired → refresh once, then replay the original request.
    // `/auth/refresh` itself is excluded, otherwise a dead session would loop.
    const isRefreshCall = original?.url?.includes('/auth/refresh');
    if (status === 401 && code === 'TOKEN_EXPIRED' && original && !original._retried && !isRefreshCall) {
      original._retried = true;

      // Concurrent 401s share a single refresh call instead of firing one each.
      // A refresh that fails for any reason but a refused session rejects with
      // its own error, which becomes this request's error — the person stays in.
      refreshPromise ??= requestNewAccessToken().finally(() => {
        refreshPromise = null;
      });

      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
    }

    // Session is genuinely gone — let the app clear its state and redirect.
    if (status === 401 && (code === 'TOKEN_INVALID' || code === 'UNAUTHENTICATED') && !isRefreshCall) {
      tokenStore.notifyExpired();
    }

    throw new ApiRequestError(
      data?.message ?? 'Something went wrong. Please try again.',
      status,
      code,
      data?.fields ?? [],
    );
  },
);

/** Unwraps the `{ success, message, data }` envelope so callers get `data`. */
export async function unwrap<T>(promise: Promise<AxiosResponse<ApiSuccess<T>>>): Promise<T> {
  const response = await promise;
  return response.data.data;
}
